import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from "react-router-dom";
import { ProductListItem } from '../models/product-list-item';
import { Product } from '../models/product';
import { BatchListItem } from '../models/batch-list-item';
import { CrudFunctions } from '../utilities/crud-functions';
import ProductFamilyComponent from './Production/ProductFamilyComponent';
import { RenderingFunctions } from '../utilities/rendering-functions';
import { DocumentationConstants } from '../store/documentation-constants';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType, ProductState, ProductLine } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import DmaFooter from './DmaFooter';

import DataGrid, {
    Column,
    FilterRow,
    Selection,
    HeaderFilter,
    Scrolling,
    SearchPanel,
    Pager,
    Paging,
    Toolbar,
    Button as ColumnButton,
    MasterDetail,
    Item,
    LoadPanel
} from 'devextreme-react/data-grid';

import SelectBox from 'devextreme-react/select-box';
import { TextBox } from 'devextreme-react/text-box';
import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

const productLines = [
    { id: ProductLine.Common, name: "Common" },
    { id: ProductLine.Environmental, name: "Environmental" },
    { id: ProductLine.ClinTox, name: "Clin/TOX" },
    { id: ProductLine.Other, name: "Other" },
    { id: ProductLine.Custom, name: "Custom" }
]

let key = 0;

export default function ProductsForm() {
    const navigate = useNavigate();

    const [products, setProducts] =
        useState<ProductListItem[]>();

    const [productBatches, setProductBatches] =
        useState<BatchListItem[]>();

    const [productSearchFilter, setProductSearchFilter] =
        useState<string>();

    const [batchSearchFilter, setBatchSearchFilter] =
        useState<string>();

    const [newProductLine, setNewProductLine] =
        useState<ProductLine>(ProductLine.Common);

    const [newProductPromptVisible, setNewProductPromptVisible] =
        useState<boolean>();

    const [addProductEnabled, setAddProductEnabled] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        setAuthorizations();
    }, []);

    key++;

    return (
        <div className="pw-content">
            <div id="products-form-container">
                <div className="lq-title-bar">
                    <div className="lq-main-title">Products</div>
                    <div className="title-bar-spacer" />

                    <div className="save-toolbar">
                        <div>
                            <NavLink to={`/documentation/${DocumentationConstants.ProductsForm}`}
                                className="pw-right-align-content" target="_blank" rel="noopener noreferrer">
                                <Button className="title-bar-button pw-orange"
                                    text="" icon="help" type="normal"
                                    hint="help and documentation" />
                            </NavLink>
                        </div>
                    </div>
                </div>

                <div className="pw-comp-row">
                    <div className="pw-comp-15">
                        <div className="pw-form-unit-label">Search by product name or CAS#:</div>
                        <TextBox inputAttr={{ id: "txtProductSearch" }}
                            elementAttr={{ id: "productSearchDiv" }}
                            className="textbox"
                            width={'20em'} data-testid="products-search-textbox"
                            onValueChanged={productSearchFilterChanged}
                            onEnterKey={onSearchEnterKeyDown}
                            placeholder="search by product name or CAS#" value={productSearchFilter} />
                    </div>

                    <div className="pw-margin-top-08">
                        <Button id="searchButton"
                            elementAttr={{ id: "searchButton" }}
                            text="Search" type="normal"
                            className="pw-margin-left-05"
                            onClick={searchButtonClicked} />

                    </div>
                </div>

                <div id="products-datagrid-container" data-testid="productsDataGrid">
                    <DataGrid id="productsDataGrid"
                        height="20rem"
                        dataSource={products != null ? products : []}
                        onSelectionChanged={productSelectionChanged}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true}
                        repaintChangesOnly={true} >

                        <LoadPanel enabled={true} />

                        <SearchPanel visible={true}
                            placeholder="Search..." />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />

                        <Paging defaultPageSize={30} />
                        <Pager
                            showPageSizeSelector={true}
                            allowedPageSizes={[30, 60, 100]}
                            showInfo={true} />

                        <Column type="buttons" width='4rem'>
                            <ColumnButton icon="edit" hint="edit product" onClick={goToNewProduct} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onProductInformationButtonClicked} />
                        </Column>

                        <Column dataField='partNumber' cellRender={partNumberRender} width="8rem" caption="Part#" dataType="string" />
                        <Column dataField='casNumber' width="8rem" caption="CAS#" dataType="string" />
                        <Column dataField='productStateName' cellRender={productStateRender} width="6rem" caption="State" dataType="string" />
                        <Column dataField='nominalUnitSize' caption="Unit Size" width="8rem" dataType="number" />
                        <Column dataField='uomName' caption="UOM" width="5rem" dataType="string" />
                        <Column dataField='nameLabel' dataType="string" />
                        
                        <MasterDetail enabled={true} component={ProductFamilyComponent} />
                        <Toolbar>
                            <Item name="searchPanel" />
                            <Item location='after'>
                                <Button id="addProductButton" type='normal' hint='Add Product' icon='plus'
                                    width={'4em'} disabled={!addProductEnabled}
                                    onClick={onAddNewProductClick} />
                            </Item>
                        </Toolbar>

                    </DataGrid>
                </div>

                <div className="pw-comp-row">
                    <div className="pw-comp-15">
                        <div className="pw-form-unit-label">Search by batch name:</div>
                        <TextBox id="txtBatchSearch" className="textbox"
                            width={'20em'}
                            onValueChanged={batchSearchFilterChanged}
                            placeholder="search by batch name" value={batchSearchFilter} />
                    </div>

                    <div className="pw-margin-top-09">
                        <Button id="searchButton" text="Search" type="normal"
                            className="pw-margin-left-05"
                            onClick={onSearchProductsByBatchName} />

                        <Button id="clearButton" text="Clear" type="normal"
                            className="pw-margin-left-05"
                            onClick={clearButtonClicked} />
                    </div>
                </div>

                <div id="product-batches-datagrid-container">
                    <DataGrid height="15rem"
                        dataSource={productBatches != null ? productBatches : []}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        showBorders={true}>

                        <LoadPanel enabled={true} />
                        <Selection mode={'single'} />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <Scrolling mode={'virtual'} />

                        <Column type="buttons" width='4rem'>
                            <ColumnButton render={gotoBatchEditCellRender} />
                            <ColumnButton render={gotoBatchProductionEditCellRender} />
                        </Column>

                        <Column dataField='batchDate' width="8rem" dataType="date" format="MM/dd/yyyy" />
                        <Column dataField='fullBatchName' dataType="string" width="12rem" />
                        <Column dataField='batchName' dataType="string" width="10rem" />
                        <Column dataField='batchVersion' dataType="number" width="8rem" />
                        <Column dataField='batchTypeName' dataType="string" width="12rem" />
                        <Column />

                        <Toolbar>
                            <Item location='before' template='batchesToolbarTemplate'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Batches</div>
                                </div>
                            </Item>
                        </Toolbar>

                    </DataGrid>
                </div>

                <DmaFooter />

                {newProductPromptVisible &&
                    <DialogPopup visible={newProductPromptVisible}
                        onHiding={hideItemBasicInformationDialog}
                        title="Create New Product"
                        width='22rem'
                        height='14rem'
                        dragEnabled={false}
                        resizeEnabled={false}>
                        <div>
                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-20">
                                    <div className="pw-form-unit-label">Product Line</div>
                                    <SelectBox className="selectbox"
                                        inputAttr={{ id: "txtNewProductLine" }}
                                        displayExpr='name' valueExpr='id'
                                        dataSource={productLines ? productLines : []}
                                        onValueChanged={onNewProductLineChanged}
                                        value={newProductLine ? newProductLine : null} />
                                </div>
                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-centered-buttons-container">
                                    <Button id="applySelectionButton"
                                        className="pw-button"
                                        text="Apply" type="success"
                                        hint="apply selection"
                                        height='2.1rem'
                                        onClick={onApplyNewProductClick} />

                                    <Button id="cancelButton"
                                        className="pw-button"
                                        text="Cancel" type="normal"
                                        hint="cancel"
                                        height='2.1rem'
                                        onClick={onCancelNewProductClick} />
                                </div>
                            </div>

                        </div>
                    </DialogPopup>
                }

                {itemBasicInformationDialogVisible &&
                    <DialogPopup visible={itemBasicInformationDialogVisible}
                        onHiding={hideItemBasicInformationDialog}
                        title="Item Basic Information"
                        width={'25rem'}
                        height={'21rem'}
                        dragEnabled={false}
                        resizeEnabled={false}>
                        <ItemBasicInformationDialog initInformation={itemBasicInformation}
                            onOkClick={hideItemBasicInformationDialog}
                            key={`IBI${key}`} />
                    </DialogPopup>
                }

                {globalDialogVisible &&
                    <GlobalDialog metadata={globalDialogMetadata} />
                }
            </div>
        </div>
    );

    function onNewProductLineChanged(e: any) {
        setNewProductLine(e.value);
    }

    async function onApplyNewProductClick() {
        if (newProductLine) {
            await addNewProduct(newProductLine)
                .then((data) => {
                    if (data.value) {
                        hideNewProductPrompt();
                        navigate(`/product/${data.value}`);
                    }
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Adding New Product");
                });
        }
    }

    function onCancelNewProductClick() {
        hideNewProductPrompt();
    }

    function onSearchEnterKeyDown(e: any) {
        searchProducts();
    }

    function goToNewProduct(e: any) {
        if (e.row && e.row.data && e.row.data.productId) {
            navigate(`/product/${e.row.data.productId}`);
        }
    }

    function onProductInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            const prod = e.row.data as Product;

            if (prod) {
                let itemInformation: ItemBasicInformation = {
                    rowPointer: prod.productId,
                    createTimestamp: prod.createTimestamp,
                    lastModifiedTimestamp: prod.modifyTimestamp,
                    createUser: prod.createUser,
                    modifyUser: prod.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function productSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            let product = e.selectedRowsData[0] as ProductListItem;

            if (product)
                getProductBatches(product);
        }
    }

    function productSearchFilterChanged(e: any) {
        setProductSearchFilter(e.value);
    }

    function batchSearchFilterChanged(e: any) {
        setBatchSearchFilter(e.value);
    }

    function onSearchProductsByBatchName() {
        if (batchSearchFilter)
            getProductsByBatchName(batchSearchFilter);
    }

    async function onAddNewProductClick() {
        showNewProductPrompt();       
    }

    // GET functions

    async function getProductsList(itemSearch: string) {
        await CrudFunctions.getData(`products/productsbysearch/${itemSearch}`)
            .then(data => {
                setProducts(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Product List");
            });
    }

    async function getProductsByBatchName(batchName: string) {
        await CrudFunctions.getData(`products/productbybatchname/${batchName}`)
            .then(data => {
                setProducts(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Products");
            });
    }

    async function getProductBatches(product: ProductListItem) {
        await CrudFunctions.getData(`products/batcheslist/${product.productId}`)
            .then(data => {
                setProductBatches(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Product Batches");
            });
    }

    async function getProductDataHierarchy(productId: string) {
        return await CrudFunctions.getData(`products/productdatahierarchy/${productId}`);
    }

    // INSERT functions

    async function addNewProduct(productLine: ProductLine) {
        return CrudFunctions.postData(`products/product/${productLine}`, null);
    }

    // navigation

    function searchProducts() {
        if (productSearchFilter && productSearchFilter.length > 2) {
            getProductsList(productSearchFilter);
        }
    }

    function searchButtonClicked(e: any) {
        searchProducts();

        /*
        if (batchSearchFilter && batchSearchFilter.length > 2) {
            getProductsByBatchName(batchSearchFilter);
        }
        */
    }

    //rendering

    function partNumberRender(e: any) {
        let product = e.data as Product;

        let cssClass = RenderingFunctions.returnProductStateColor(product.productState);

        if (product) {
            return <div id={`product_${product.productId}`} className={cssClass}>{product.partNumber}</div>;
        }
        else
            return <div></div>;
    }

    function productStateRender(e: any) {
        let product = e.data as Product;

        let cssClass = RenderingFunctions.returnProductStateColor(product.productState);

        if (product) {
            return <div className={cssClass}>{product.productStateName}</div>;
        }
        else
            return <div></div>;
    }

    function gotoBatchEditCellRender(e: any) {
        let batch = e.data as BatchListItem;
        let path = `/batch/${batch.batchId}`;

        if (batch) {
            return <NavLink to={path} className="datagrid-icon-link">
                <span className="dx-icon-edit datagrid-icon column-button-margin"></span>
            </NavLink>;
        }
    }

    function gotoBatchProductionEditCellRender(e: any) {
        let batch = e.data as BatchListItem;
        let path = `/batch-production/${batch.batchId}`;

        if (batch) {
            return <NavLink to={path} className="datagrid-icon-link">
                <span className="dx-icon-product datagrid-icon column-button-margin"></span>
            </NavLink>;
        }
    }

    //show/hide

    function showNewProductPrompt() {
        setNewProductPromptVisible(true);
    }

    function hideNewProductPrompt() {
        setNewProductPromptVisible(false);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    //other

    function clearButtonClicked(e: any) {
        setBatchSearchFilter("");
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function setAuthorizations() {
        //todo:
        /*
        const user = UserDataStore.getCurrentUser();

        const addProductEnabled = user && user.roleCodes &&
            user.roleCodes.includes('SA-A');

        */

        setAddProductEnabled(true);
    }

}

